/**
 * BundlePicker – "Kup w pakiecie" feature for z-okienkami products.
 *
 * v2 changes:
 * 1.  Current product is selectable/deselectable – NOT locked in.
 * 2.  Single shared sessionStorage key → config preserved across pages.
 * 3.  Auto-adds current product on FIRST fresh start only.
 * 4-6. Handled server-side (dynamic discount, no coupons).
 * 7.  Each product can be picked in multiple quantities (occupies multiple slots).
 * 8.  Quantity input capped at available free slots.
 * 9.  Main/current product removable.
 */
export const BundlePicker = function () {

    const $picker = $('#bundle-picker');
    if (!$picker.length) return;

    // ── Data from PHP ──
    const currentId        = parseInt($picker.data('current-id'));
    const currentPrice     = parseFloat($picker.data('current-price'));
    const currentTitle     = $picker.data('current-title');
    const currentThumb     = $picker.data('current-thumb');
    const currentPriceHtml = $picker.data('current-price-html') || (currentPrice.toFixed(2).replace('.', ',') + ' zł');
    const nonce            = $picker.data('nonce');
    const ajaxUrl          = $picker.data('ajax-url');
    const otherProducts    = $picker.data('products') || [];
    const isConfig         = $picker.data('is-config') === 1 || $picker.data('is-config') === '1';

    // Current product at the top of the list
    const currentProductData = {
        id:         currentId,
        title:      currentTitle,
        price:      currentPrice,
        price_html: currentPriceHtml,
        thumbnail:  currentThumb,
        permalink:  window.location.href,
    };
    // On configurator pages (is_prod_config) the current product is excluded from the list
    const allProducts = isConfig ? otherProducts : [currentProductData].concat(otherProducts);

    // ── Shared localStorage key (preserved across all tabs / product pages) ──
    const STORAGE_KEY = 'wbs_bundle_session';
    const PLUS_MODE   = -1; // sentinel for "4 i więcej" – no upper slot cap

    // ── State ──
    let bundleSize    = 0;   // 2, 3, or PLUS_MODE (-1)
    let selectedItems = [];  // [{id: N, qty: N}, ...]

    function getDiscount(size) {
        if (size === PLUS_MODE || size >= 4) return 0.15;
        if (size === 3) return 0.10;
        if (size === 2) return 0.05;
        return 0;
    }
    function hasFreeShipping(size) { return size === PLUS_MODE || size >= 3; }

    // ────────────────────────────────────────────────────────────
    // Helpers
    // ────────────────────────────────────────────────────────────
    function totalSlotsUsed() {
        return selectedItems.reduce(function (s, i) { return s + i.qty; }, 0);
    }
    function freeSlots() {
        return bundleSize === PLUS_MODE ? 999 : bundleSize - totalSlotsUsed();
    }
    /** Bundle is "complete" and ready to add to cart */
    function isComplete() {
        if (!bundleSize) return false;
        if (bundleSize === PLUS_MODE) return totalSlotsUsed() >= 4;
        return totalSlotsUsed() === bundleSize;
    }

    function getSelectedItem(id) {
        return selectedItems.find(function (i) { return i.id === id; }) || null;
    }
    function getSelectedQty(id) {
        const item = getSelectedItem(id);
        return item ? item.qty : 0;
    }
    function getProductData(pid) {
        return allProducts.find(function (p) { return p.id === pid; }) || null;
    }
    function formatPrice(val) {
        return val.toFixed(2).replace('.', ',') + ' zł';
    }

    /** Trim selectedItems so total qty ≤ cap (skip when PLUS_MODE) */
    function trimToSize(cap) {
        if (cap === PLUS_MODE) return; // no cap in plus mode
        let remaining = cap;
        selectedItems = selectedItems.reduce(function (acc, item) {
            if (remaining <= 0) return acc;
            const qty = Math.min(item.qty, remaining);
            remaining -= qty;
            acc.push({ id: item.id, qty: qty });
            return acc;
        }, []);
    }

    // ────────────────────────────────────────────────────────────
    // Cookie state
    // ────────────────────────────────────────────────────────────
    function loadState() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) return JSON.parse(raw);
        } catch (e) {}
        return null;
    }
    function saveState() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ bundleSize: bundleSize, selectedItems: selectedItems }));
        } catch (e) {}
        syncUrl();
    }
    function clearState() {
        try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
        clearUrl();
    }


    /** Serialize current state into URL and push via replaceSta te (no history entry). */
    function syncUrl() {
        if (!bundleSize || !selectedItems.length) { clearUrl(); return; }

        const params = new URLSearchParams(window.location.search);
        params.set('bundle_size', bundleSize === PLUS_MODE ? '4plus' : bundleSize);
        params.set('bundle_items', selectedItems.map(function (i) { return i.id + ':' + i.qty; }).join(','));

        const newUrl = window.location.pathname + '?' + params.toString() + window.location.hash;
        try { history.replaceState(null, '', newUrl); } catch (e) {}
    }

    /** Remove bundle params from URL without reloading. */
    function clearUrl() {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('bundle_size') && !params.has('bundle_items')) return;
        params.delete('bundle_size');
        params.delete('bundle_items');
        const qs      = params.toString();
        const newUrl  = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
        try { history.replaceState(null, '', newUrl); } catch (e) {}
    }

    /**
     * Read bundle state from URL params.
     * Returns {bundleSize, selectedItems} or null.
     */
    function readUrlParams() {
        const params = new URLSearchParams(window.location.search);
        const sizeRaw  = params.get('bundle_size');
        const itemsRaw = params.get('bundle_items');
        if (!sizeRaw || !itemsRaw) return null;

        let size;
        if (sizeRaw === '4plus') {
            size = PLUS_MODE;
        } else {
            size = parseInt(sizeRaw);
            if (size < 2) return null;
        }

        const items = [];
        itemsRaw.split(',').forEach(function (chunk) {
            const parts = chunk.split(':');
            if (parts.length !== 2) return;
            const id  = parseInt(parts[0]);
            const qty = parseInt(parts[1]);
            if (id > 0 && qty > 0) items.push({ id: id, qty: qty });
        });

        if (!items.length) return null;
        return { bundleSize: size, selectedItems: items };
    }

    // ────────────────────────────────────────────────────────────
    // Restore state (URL params take priority over sessionStorage)
    // ────────────────────────────────────────────────────────────
    function restoreState() {
        // 1. Try URL params first (shareable links)
        const urlState = readUrlParams();
        const state    = urlState || loadState();

        if (!state || !state.bundleSize || !Array.isArray(state.selectedItems) || !state.selectedItems.length) return;
        if (state.bundleSize !== PLUS_MODE && state.bundleSize < 2) return;

        // Keep ALL items from cookie (not only those available on current page).
        // Items from other pages are preserved in state so cookie survives navigation.
        const allItems = state.selectedItems
            .filter(function (si) { return si.id > 0 && si.qty > 0; })
            .map(function (si) { return { id: si.id, qty: si.qty }; });

        if (!allItems.length) return;

        bundleSize    = state.bundleSize;
        selectedItems = allItems;
        trimToSize(bundleSize);

        // If restored from URL, also persist to cookie so navigation works
        if (urlState) saveState();

        activateSizeBtn(bundleSize);

        // Only open panel if at least one selected item is available on this page
        const availableIds = allProducts.map(function (p) { return p.id; });
        const hasLocal = selectedItems.some(function (si) { return availableIds.includes(si.id); });
        if (hasLocal) openPanel();
    }

    // ────────────────────────────────────────────────────────────
    // Size-button savings display
    // ────────────────────────────────────────────────────────────
    function updateSizeSavings() {
        $picker.find('.bundle-picker__size-btn').each(function () {
            const sizeAttr = $(this).data('size');
            if (sizeAttr === '4plus') return; // static text in PHP
            const size  = parseInt(sizeAttr);
            const saved = (currentPrice * size) * getDiscount(size);
            const $save = $(this).find('.bundle-picker__size-save');
            if (saved > 0) $save.text('Oszczędzasz ' + formatPrice(saved));
        });
    }

    // ────────────────────────────────────────────────────────────
    // Init
    // ────────────────────────────────────────────────────────────
    function init() {
        updateSizeSavings();
        bindEvents();
        initStickyObserver();
        restoreState();
        renderFullGrid(); // initial render (shows hint when no size chosen yet)
    }

    // ────────────────────────────────────────────────────────────
    // Events
    // ────────────────────────────────────────────────────────────
    function bindEvents() {

        // Size buttons
        $picker.on('click', '.bundle-picker__size-btn', function () {
            const sizeAttr = $(this).data('size');
            const newSize  = (sizeAttr === '4plus') ? PLUS_MODE : parseInt(sizeAttr);

            if (bundleSize === newSize && $('#bundle-picker-panel').is(':visible')) {
                closePanel();
                return;
            }

            // First click ever (no prior state) → auto-add current product as slot 1
            // BUT skip this on configurator products (is_prod_config) – start empty
            const isFirstClick = (bundleSize === 0 && selectedItems.length === 0);

            bundleSize = newSize;

            if (isFirstClick && !isConfig) {
                selectedItems = [{ id: currentId, qty: 1 }];
            } else {
                trimToSize(bundleSize);
            }

            saveState();
            activateSizeBtn(newSize);
            openPanel();
        });

        // Close panel
        $picker.on('click', '.bundle-picker__panel-close', function () {
            closePanel();
        });

        // Toggle product row (add/remove)
        $picker.on('click', '.bundle-picker__product-item', function (e) {
            if ($(e.target).closest('.bundle-picker__product-view').length) return;
            if ($(e.target).closest('.bundle-picker__product-qty').length) return;
            e.preventDefault();

            const pid = parseInt($(this).data('product-id'));
            if (getSelectedQty(pid) > 0) {
                selectedItems = selectedItems.filter(function (i) { return i.id !== pid; });
            } else {
                if (freeSlots() <= 0) return;
                selectedItems.push({ id: pid, qty: 1 });
            }
            saveState();
            renderPanel();
        });

        // Prevent product name text from toggling row
        $picker.on('click', '.bundle-picker__product-name', function (e) {
            e.preventDefault();
            e.stopPropagation();
        });

        // Remove from selected bar (removes entire product, all qty)
        $picker.on('click', '.bundle-picker__selected-remove', function (e) {
            e.stopPropagation();
            const pid = parseInt($(this).data('product-id'));
            selectedItems = selectedItems.filter(function (i) { return i.id !== pid; });
            saveState();
            renderPanel();
        });

        // Qty minus
        $picker.on('click', '.bundle-picker__qty-minus', function (e) {
            e.stopPropagation();
            const pid  = parseInt($(this).closest('.bundle-picker__product-item').data('product-id'));
            const item = getSelectedItem(pid);
            if (!item) return;
            if (item.qty <= 1) {
                selectedItems = selectedItems.filter(function (i) { return i.id !== pid; });
            } else {
                item.qty--;
            }
            saveState();
            renderPanel();
        });

        // Qty plus
        $picker.on('click', '.bundle-picker__qty-plus', function (e) {
            e.stopPropagation();
            if ($(this).prop('disabled')) return;
            const pid  = parseInt($(this).closest('.bundle-picker__product-item').data('product-id'));
            const item = getSelectedItem(pid);
            if (!item || freeSlots() <= 0) return;
            item.qty++;
            saveState();
            renderPanel();
        });

        // Add to cart (main button)
        $picker.on('click', '#bundle-add-to-cart', function () {
            addBundleToCart($(this));
        });

        // Add to cart (sticky bar button)
        $(document).on('click', '#bundle-sticky-add', function () {
            addBundleToCart($(this));
        });

        // Full-width grid qty controls
        $(document).on('click', '.bundle-full-grid__qty-minus', function (e) {
            e.stopPropagation();
            const pid  = parseInt($(this).closest('.bundle-full-grid__item').data('product-id'));
            const item = getSelectedItem(pid);
            if (!item) return;
            if (item.qty <= 1) {
                selectedItems = selectedItems.filter(function (i) { return i.id !== pid; });
            } else {
                item.qty--;
            }
            saveState();
            renderPanel();
            if (!$('#bundle-picker-panel').is(':visible')) openPanel();
        });

        $(document).on('click', '.bundle-full-grid__qty-plus', function (e) {
            e.stopPropagation();
            if ($(this).prop('disabled')) return;
            const pid  = parseInt($(this).closest('.bundle-full-grid__item').data('product-id'));
            const item = getSelectedItem(pid);
            if (!item || freeSlots() <= 0) return;
            item.qty++;
            saveState();
            renderPanel();
            if (!$('#bundle-picker-panel').is(':visible')) openPanel();
        });

        // Full-width grid item click (is_prod_config page)
        $(document).on('click', '.bundle-full-grid__item', function (e) {
            if ($(e.target).closest('.bundle-full-grid__qty-minus').length) return;
            if ($(e.target).closest('.bundle-full-grid__qty-plus').length) return;
            if ($(e.target).closest('.bundle-full-grid__product-view').length) return;
            if (!bundleSize) {
                // No size chosen yet → scroll to picker and highlight it
                var pickerTop = ($picker.offset() || {}).top;
                if (pickerTop) $('html,body').animate({ scrollTop: pickerTop - 80 }, 400);
                $picker.find('.bundle-picker__sizes').addClass('bundle-picker__sizes--flash');
                setTimeout(function () {
                    $picker.find('.bundle-picker__sizes').removeClass('bundle-picker__sizes--flash');
                }, 1200);
                return;
            }
            const pid = parseInt($(this).data('product-id'));
            if (getSelectedQty(pid) > 0) {
                selectedItems = selectedItems.filter(function (i) { return i.id !== pid; });
            } else {
                if (freeSlots() <= 0) return;
                selectedItems.push({ id: pid, qty: 1 });
            }
            saveState();
            renderPanel();
            if (!$('#bundle-picker-panel').is(':visible')) openPanel();
        });
    }


    function activateSizeBtn(size) {
        $picker.find('.bundle-picker__size-btn').removeClass('bundle-picker__size-btn--active');
        const attr = (size === PLUS_MODE) ? '4plus' : size;
        $picker.find('.bundle-picker__size-btn[data-size="' + attr + '"]').addClass('bundle-picker__size-btn--active');
    }
    function openPanel()  { renderPanel(); $('#bundle-picker-panel').slideDown(300); }
    function closePanel() { $('#bundle-picker-panel').slideUp(300); }

    // ────────────────────────────────────────────────────────────
    // Full-width product grid (is_prod_config pages only)
    // ────────────────────────────────────────────────────────────
    function renderFullGrid() {
        const $grid    = $('#bundle-full-grid-items');
        const $section = $('.bundle-full-grid__section');
        if (!$grid.length) return;

        const noSize   = !bundleSize;
        const free     = freeSlots();
        let html       = '';

        // Always render all product cards
        otherProducts.forEach(function (p) {
            const qty        = getSelectedQty(p.id);
            const isSelected = qty > 0;
            // When no size yet – all cards are visually grayed (handled by overlay + CSS)
            const noRoom     = !noSize && !isSelected && free <= 0;

            html += '<div class="bundle-full-grid__item'
                + (isSelected ? ' bundle-full-grid__item--selected' : '')
                + (noRoom     ? ' bundle-full-grid__item--disabled'  : '')
                + '" data-product-id="' + p.id + '">';

            // Check indicator (top-right)
            html += '<div class="bundle-full-grid__check">';
            if (isSelected) {
                html += '<span class="bundle-full-grid__check-icon">✓</span>';
            }
            html += '</div>';

            // Product view link (top-left)
            html += '<a href="' + p.permalink + '" class="bundle-full-grid__product-view" target="_blank" title="Zobacz książkę">';
            html += '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
            html += '</a>';

            // Thumbnail
            html += '<div class="bundle-full-grid__image-wrap">';
            html += '<img src="' + p.thumbnail + '" alt="' + p.title + '" loading="lazy" />';
            html += '</div>';

            // Title
            html += '<div class="bundle-full-grid__name">' + p.title + '</div>';

            // Qty controls (visible only when selected)
            if (isSelected) {
                const plusDisabled = free <= 0;
                const isLastUnit   = qty === 1;
                const minusIcon    = isLastUnit
                    ? '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>'
                    : '−';
                html += '<div class="bundle-full-grid__qty">';
                html += '<button type="button" class="bundle-full-grid__qty-minus'
                    + (isLastUnit ? ' bundle-full-grid__qty-minus--remove' : '')
                    + '" title="' + (isLastUnit ? 'Usuń' : 'Zmniejsz') + '">' + minusIcon + '</button>';
                html += '<span class="bundle-full-grid__qty-value">' + qty + '</span>';
                html += '<button type="button" class="bundle-full-grid__qty-plus'
                    + (plusDisabled ? ' bundle-full-grid__qty-plus--disabled' : '') + '"'
                    + (plusDisabled ? ' disabled' : '') + ' title="Zwiększ">+</button>';
                html += '</div>';
            }

            html += '</div>';
        });

        $grid.html(html);

        // ── Overlay: show when no size selected ──
        $section.find('.bundle-full-grid__overlay').remove();
        if (noSize) {
            $section.append(
                '<div class="bundle-full-grid__overlay">' +
                    '<div class="bundle-full-grid__overlay-msg">' +
                        '<strong>Najpierw wybierz<br>ilość sztuk w konfiguratorze</strong>' +
                    '</div>' +
                '</div>'
            );
        }
    }


    function renderPanel() {
        const used   = totalSlotsUsed();
        const free   = freeSlots();
        const $panel = $('#bundle-picker-panel');

        // Counter display
        if (bundleSize === PLUS_MODE) {
            $panel.find('.bundle-picker__panel-counter').html('(' + used + ' szt.)');
            $panel.find('.bundle-picker__panel-max').text('4+');
        } else {
            $panel.find('.bundle-picker__panel-counter').html('(' + used + ' / ' + bundleSize + ')');
            $panel.find('.bundle-picker__panel-max').text(bundleSize);
        }

        // ── Selected bar ──
        const slots = [];
        selectedItems.forEach(function (item) {
            for (let q = 0; q < item.qty; q++) slots.push(item.id);
        });

        // In PLUS_MODE show selected items + 1 extra empty slot (invite adding more)
        const displaySlots = (bundleSize === PLUS_MODE) ? slots.length + 1 : bundleSize;

        let selectedHtml = '';
        for (let si = 0; si < displaySlots; si++) {
            if (si < slots.length) {
                const p = getProductData(slots[si]);
                if (!p) continue;
                selectedHtml += '<div class="bundle-picker__selected-item">';
                selectedHtml += '<img src="' + p.thumbnail + '" alt="' + p.title + '" />';
                selectedHtml += '<button type="button" class="bundle-picker__selected-remove" data-product-id="' + p.id + '" title="Usuń">&times;</button>';
                selectedHtml += '<span class="bundle-picker__selected-title">' + p.title + '</span>';
                selectedHtml += '</div>';
            } else {
                selectedHtml += '<div class="bundle-picker__selected-item bundle-picker__selected-item--empty">';
                selectedHtml += '<span class="bundle-picker__selected-placeholder">' + (bundleSize === PLUS_MODE && si >= 3 ? '+' : '?') + '</span>';
                selectedHtml += '<span class="bundle-picker__selected-title">' + (bundleSize === PLUS_MODE ? 'Dodaj kolejną' : 'Wybierz książkę') + '</span>';
                selectedHtml += '</div>';
            }
        }
        $('#bundle-picker-selected').html(selectedHtml);

        // ── Products list ──
        let productsHtml = '';
        allProducts.forEach(function (p) {
            const qty        = getSelectedQty(p.id);
            const isSelected = qty > 0;
            const isCurrent  = p.id === currentId;
            const noRoom     = !isSelected && free <= 0;

            productsHtml += '<div class="bundle-picker__product-item'
                + (isSelected ? ' bundle-picker__product-item--selected' : '')
                + (noRoom     ? ' bundle-picker__product-item--disabled'  : '')
                + '" data-product-id="' + p.id + '">';

            productsHtml += '<div class="bundle-picker__product-thumb">';
            productsHtml += '<img src="' + p.thumbnail + '" alt="' + p.title + '" loading="lazy" />';
            productsHtml += '<div class="bundle-picker__product-check">' + (isSelected ? '✓' : '') + '</div>';
            productsHtml += '</div>';

            productsHtml += '<div class="bundle-picker__product-info">';
            productsHtml += '<span class="bundle-picker__product-name">' + p.title + '</span>';
            productsHtml += '<div class="bundle-picker__product-price">' + p.price_html + '</div>';
            productsHtml += '</div>';

            if (isSelected) {
                const plusDisabled = free <= 0;
                const isLastUnit   = qty === 1;
                const minusIcon    = isLastUnit
                    ? '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>'
                    : '−';
                const minusTitle   = isLastUnit ? 'Usuń z pakietu' : 'Zmniejsz ilość';
                const minusClass   = 'bundle-picker__qty-minus' + (isLastUnit ? ' bundle-picker__qty-minus--remove' : '');

                productsHtml += '<div class="bundle-picker__product-qty" data-product-id="' + p.id + '">';
                productsHtml += '<button type="button" class="' + minusClass + '" title="' + minusTitle + '" aria-label="' + minusTitle + '">' + minusIcon + '</button>';
                productsHtml += '<input type="number" class="bundle-picker__qty-input" value="' + qty + '" min="1" max="' + (qty + (plusDisabled ? 0 : freeSlots())) + '" data-product-id="' + p.id + '" aria-label="Ilość: ' + p.title + '" readonly />';
                productsHtml += '<button type="button" class="bundle-picker__qty-plus'
                    + (plusDisabled ? ' bundle-picker__qty-plus--disabled' : '') + '"'
                    + (plusDisabled ? ' disabled' : '') + ' title="Zwiększ ilość" aria-label="Zwiększ ilość">+</button>';
                productsHtml += '</div>';
            }

            productsHtml += '<a href="' + p.permalink + '" class="bundle-picker__product-view" target="_blank" title="Zobacz książkę">';
            productsHtml += '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
            productsHtml += '</a>';
            productsHtml += '</div>';
        });
        $('#bundle-picker-products').html(productsHtml);

        if (isComplete()) {
            renderSummary();
            $('#bundle-picker-summary').slideDown(200);
        } else {
            $('#bundle-picker-summary').slideUp(200);
        }

        renderFullGrid();
    }

    function renderSummary() {
        let totalOriginal = 0;
        selectedItems.forEach(function (selItem) {
            const p = getProductData(selItem.id);
            if (p) totalOriginal += p.price * selItem.qty;
        });

        const disc        = getDiscount(bundleSize === PLUS_MODE ? totalSlotsUsed() : bundleSize);
        const totalDisc   = totalOriginal * (1 - disc);
        const saved       = totalOriginal - totalDisc;
        const hasFreeShip = hasFreeShipping(bundleSize);

        // Update existing PHP-rendered elements – do NOT replace the HTML structure
        $('#bundle-save-amount').text(formatPrice(saved) + ' (' + Math.round(disc * 100) + '%)');
        $('#bundle-original-price').text(formatPrice(totalOriginal));
        $('#bundle-final-price').text(formatPrice(totalDisc));
        $('#bundle-free-shipping').toggle(hasFreeShip);
        // Reset button state in case it was disabled from a previous failed attempt
        $('#bundle-add-to-cart').prop('disabled', false).find('span').first().text('Dodaj zestaw do koszyka');
        $('.bundle-picker__loader').hide();

        // Update sticky bar content
        updateStickyBar(totalOriginal, totalDisc, saved, disc);
    }

    // ────────────────────────────────────────────────────────────
    // Sticky bar
    // ────────────────────────────────────────────────────────────
    let _pickerInViewport = true; // assume visible on load

    function initStickyObserver() {
        if (!window.IntersectionObserver || !document.getElementById('bundle-sticky-bar')) return;
        const pickerEl = $picker[0];
        if (!pickerEl) return;
        const observer = new IntersectionObserver(function (entries) {
            _pickerInViewport = entries[0].isIntersecting;
            refreshStickyVisibility();
        }, { threshold: 0.1 });
        observer.observe(pickerEl);
    }

    function refreshStickyVisibility() {
        const $bar       = $('#bundle-sticky-bar');
        if (!$bar.length) return;
        const isCompleteFlag = isComplete();
        if (isCompleteFlag && !_pickerInViewport) {
            $bar.addClass('bundle-sticky-bar--visible');
        } else {
            $bar.removeClass('bundle-sticky-bar--visible');
        }
    }

    function updateStickyBar(totalOriginal, totalDisc, saved, disc) {
        $('#bundle-sticky-save').text('Oszczędzasz ' + formatPrice(saved) + ' (' + Math.round(disc * 100) + '%)');
        $('#bundle-sticky-price').text('Cena pakietu: ' + formatPrice(totalDisc));
        // Reset sticky button state
        $('#bundle-sticky-add').prop('disabled', false).find('span').first().text('Dodaj zestaw do koszyka');
        refreshStickyVisibility();
    }

    // ────────────────────────────────────────────────────────────
    // Add to cart via AJAX
    // ────────────────────────────────────────────────────────────
    function addBundleToCart($btn) {
        const $label   = $btn.find('span').first();
        const $spinner = $btn.find('.bundle-picker__loader');

        $btn.prop('disabled', true);
        $label.text('Dodawanie…');
        $spinner.show();

        // Build flat product_ids array: duplicate IDs = qty > 1 (matches PHP array_count_values logic)
        const productIds = [];
        selectedItems.forEach(function (item) {
            for (let q = 0; q < item.qty; q++) {
                productIds.push(item.id);
            }
        });

        $.post(ajaxUrl, {
            action:      'wbs_add_bundle_to_cart',
            nonce:       nonce,
            bundle_size: bundleSize === PLUS_MODE ? totalSlotsUsed() : bundleSize,
            product_ids: productIds,
        })
        .done(function (response) {
            if (response && response.success) {
                clearState();
                bundleSize    = 0;
                selectedItems = [];
                $picker.find('.bundle-picker__size-btn').removeClass('bundle-picker__size-btn--active');
                closePanel();
                $('#bundle-sticky-bar').removeClass('bundle-sticky-bar--visible');

                if (response.data && response.data.fragments) {
                    $.each(response.data.fragments, function (key, value) {
                        $(key).replaceWith(value);
                    });
                }

                $(document.body).trigger('wc_fragment_refresh');
                $(document.body).trigger('added_to_cart', [
                    response.data ? response.data.fragments : {},
                    response.data ? response.data.cart_hash : '',
                ]);

                if (response.data && response.data.cart_url) {
                    window.location.href = response.data.cart_url;
                }
            } else {
                const msg = (response && response.data && response.data.message)
                    ? response.data.message
                    : 'Wystąpił błąd. Spróbuj ponownie.';
                alert(msg);
                $btn.prop('disabled', false);
                $label.text('Dodaj zestaw do koszyka');
                $spinner.hide();
            }
        })
        .fail(function () {
            alert('Błąd połączenia. Spróbuj ponownie.');
            $btn.prop('disabled', false);
            $label.text('Dodaj zestaw do koszyka');
            $spinner.hide();
        });
    }

    init();
};
